import { z } from "zod";

/**
 * 주문문의 카테고리 — 세션 생성(첫 메시지) 시 결정되며 이후 불변
 */
export const OrderRevisionRequestCategorySchema = z.enum([
  "GENERAL_INQUIRY",
  "REVISION_REQUEST",
]);
export type OrderRevisionRequestCategory = z.infer<
  typeof OrderRevisionRequestCategorySchema
>;

export const OrderRevisionRequestCategoryColors: Record<
  OrderRevisionRequestCategory,
  { bg: string; text: string }
> = {
  GENERAL_INQUIRY: { bg: "#F3F4F6", text: "#6B7280" },
  REVISION_REQUEST: { bg: "#E8EFFF", text: "#4285F4" },
};

export const OrderRevisionRequestCategoryLabels: Record<
  OrderRevisionRequestCategory,
  string
> = {
  GENERAL_INQUIRY: "일반문의",
  REVISION_REQUEST: "수정요청",
};

/**
 * 메시지 발신자 — 고객(CUSTOMER) / 관리자(ADMIN)
 */
export const OrderRevisionMessageSenderTypeSchema = z.enum([
  "CUSTOMER",
  "ADMIN",
]);
export type OrderRevisionMessageSenderType = z.infer<
  typeof OrderRevisionMessageSenderTypeSchema
>;

/**
 * 주문문의 세션 메시지 — CUSTOMER/ADMIN이 시간순으로 쌓이는 평탄한 대화 로그
 */
export const OrderRevisionMessageSchema = z.object({
  id: z.number(),
  senderType: OrderRevisionMessageSenderTypeSchema,
  senderId: z.number(),
  senderName: z.string().nullable().optional(),
  content: z.string(),
  editedAt: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type OrderRevisionMessage = z.infer<typeof OrderRevisionMessageSchema>;

/**
 * 주문문의 세션 — 주문 1건당 세션 1개.
 * closedAt이 있으면 종료(답변완료), 없으면 진행중 — 메시지가 추가되면(발신자 무관) 재오픈됨.
 * needsRework는 종료 시점에 결정되며(문의종료=false/재작업결정=true) 재오픈돼도 값 유지.
 */
export const OrderRevisionSessionSchema = z.object({
  id: z.number(),
  orderId: z.number(),
  orderNumber: z.string().nullable().optional(),
  orderCreatedAt: z.string().nullable().optional(),
  productName: z.string(),
  companyId: z.number(),
  purchaserName: z.string().nullable().optional(),
  purchaserEmail: z.string().nullable().optional(),
  category: OrderRevisionRequestCategorySchema,
  closedAt: z.string().nullable().optional(),
  needsRework: z.boolean(),
  unreadCountForAdmin: z.number(),
  unreadCountForCustomer: z.number(),
  messages: z.array(OrderRevisionMessageSchema),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});
export type OrderRevisionSession = z.infer<typeof OrderRevisionSessionSchema>;

/**
 * 메시지 전송 요청
 */
export interface ReplyOrderRevisionRequestRequest {
  replyContent: string;
}

/**
 * 세션 종료/재작업 결정 요청 — needsRework=false면 "문의 종료", true면 "재작업 결정"
 */
export interface CloseOrderRevisionSessionRequest {
  needsRework: boolean;
}

/**
 * 세션 게시판 메인 항목 (관리자 목록 화면 - 주문당 1행, 가벼운 요약 정보)
 */
export const OrderRevisionBoardItemSchema = z.object({
  id: z.number(),
  orderId: z.number(),
  orderNumber: z.string().nullable().optional(),
  orderCreatedAt: z.string().nullable().optional(),
  productName: z.string(),
  purchaserName: z.string().nullable().optional(),
  purchaserEmail: z.string().nullable().optional(),
  category: OrderRevisionRequestCategorySchema,
  closedAt: z.string().nullable().optional(),
  needsRework: z.boolean(),
  unreadCountForAdmin: z.number(),
  unreadCountForCustomer: z.number(),
  messageCount: z.number(),
  latestActivityAt: z.string(),
});
export type OrderRevisionBoardItem = z.infer<
  typeof OrderRevisionBoardItemSchema
>;

/**
 * 세션 게시판 목록 응답 (페이징)
 */
export interface OrderRevisionBoardPageResponse {
  content: OrderRevisionBoardItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
